import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errText } from "@/lib/api";
import { fmtDateTime, tzCity, utcToZonedParts, viewerTz, zonedToUtc } from "@/lib/time";
import { TimezoneSelect } from "./TimezoneSelect";

const field = "w-full border border-black/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0A0A0A]";
export const LEADS = [[0, "No warning"], [60, "1 hour before"], [180, "3 hours before"], [720, "12 hours before"], [1440, "1 day before"], [2880, "2 days before"], [10080, "1 week before"]];
const SAMPLES = ["Europe/London", "America/New_York", "America/Los_Angeles", "Asia/Dubai", "Australia/Sydney", "UTC"];

const initial = (w) => {
  const tz = w?.timezone || viewerTz();
  if (w) {
    const start = zonedToUtc(w.start_local, tz);
    const end = utcToZonedParts(new Date(start.getTime() + w.duration_minutes * 60000), tz);
    return { title: w.title, message: w.message || "", tz, sDate: w.start_local.slice(0, 10), sTime: w.start_local.slice(11, 16), eDate: end.date, eTime: end.time,
      repeat: w.repeat || "none", until: w.repeat_until || "", lead: w.banner_lead_minutes ?? "default", runJobs: w.run_jobs !== false, notify: false };
  }
  const t = utcToZonedParts(new Date(Date.now() + 86400000), tz);
  return { title: "Scheduled maintenance", message: "", tz, sDate: t.date, sTime: "02:00", eDate: t.date, eTime: "04:00", repeat: "none", until: "", lead: "default", runJobs: true, notify: false };
};

const Field = ({ label, children, hint }) => (
  <label className="block"><span className="mb-1.5 block text-sm font-bold">{label}</span>{children}{hint && <span className="mt-1 block text-xs text-[#57534E]">{hint}</span>}</label>
);

const WorldTimes = ({ start, end, tz }) => {
  if (!start || !end) return null;
  const zones = [...new Set([tz, viewerTz(), ...SAMPLES])].slice(0, 6);
  return (
    <div className="border border-black/10 bg-[#F3F2EF] p-4" data-testid="schedule-world-times">
      <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-[#57534E]"><Globe2 className="h-4 w-4" />Visitors see it in their own time</p>
      <ul className="grid gap-1 text-sm sm:grid-cols-2">
        {zones.map((z) => <li key={z} className="flex justify-between gap-3"><span className="text-[#57534E]">{tzCity(z)}</span><span className="font-semibold">{fmtDateTime(start.toISOString(), z)}</span></li>)}
      </ul>
    </div>
  );
};

export const ScheduleDialog = ({ open, onOpenChange, window: win, defaults, subscriberCount, onSaved }) => {
  const [f, setF] = useState(() => initial(win));
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initial(win)); return undefined; }, [open, win]);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const start = zonedToUtc(`${f.sDate}T${f.sTime}`, f.tz);
  const end = zonedToUtc(`${f.eDate}T${f.eTime}`, f.tz);
  const minutes = start && end ? Math.round((end - start) / 60000) : 0;

  const save = async () => {
    if (!f.title.trim()) return toast.error("Give the maintenance a title");
    if (minutes < 5) return toast.error("The end must be at least 5 minutes after the start");
    setBusy(true);
    const body = { title: f.title.trim(), message: f.message.trim(), start_local: `${f.sDate}T${f.sTime}`, timezone: f.tz, duration_minutes: minutes,
      repeat: f.repeat, repeat_until: f.repeat !== "none" && f.until ? f.until : null, banner_lead_minutes: f.lead === "default" ? null : Number(f.lead), run_jobs: f.runJobs, notify_subscribers: f.notify };
    try {
      const r = win ? await api.put(`/maintenance/windows/${win.id}`, body) : await api.post("/maintenance/windows", body);
      toast.success(win ? "Maintenance updated" : "Maintenance scheduled");
      if (r.data?.notice === "sent") toast.success("Emailing your mailing list about it");
      else if (r.data?.notice) toast.warning(`Email notice not sent: ${r.data.notice}`);
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const defLead = LEADS.find(([v]) => v === defaults?.banner_lead_minutes)?.[1] || "default";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="schedule-dialog" className="max-h-[92vh] overflow-y-auto rounded-none bg-[#FBF9F5] sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">{win ? "Edit maintenance" : "Schedule maintenance"}</DialogTitle>
          <DialogDescription>Set it in your own time zone. It starts and ends on its own, and every visitor sees the times in their local time.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <Field label="Title (shown to visitors)"><input value={f.title} maxLength={120} onChange={(e) => set({ title: e.target.value })} className={field} data-testid="schedule-title" /></Field>
          <Field label="Message for visitors" hint="Leave empty to use your default maintenance message."><textarea value={f.message} maxLength={1000} rows={3} onChange={(e) => set({ message: e.target.value })} className={field} data-testid="schedule-message" /></Field>
          <Field label="Your time zone"><TimezoneSelect value={f.tz} onChange={(tz) => set({ tz })} testId="schedule-timezone" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Starts"><div className="flex gap-2"><input type="date" value={f.sDate} onChange={(e) => set({ sDate: e.target.value, eDate: e.target.value > f.eDate ? e.target.value : f.eDate })} className={field} data-testid="schedule-start-date" /><input type="time" value={f.sTime} onChange={(e) => set({ sTime: e.target.value })} className={`${field} w-32`} data-testid="schedule-start-time" /></div></Field>
            <Field label="Ends" hint={minutes > 0 ? `Lasts ${Math.floor(minutes / 60)}h ${minutes % 60}m` : "Must be after the start"}><div className="flex gap-2"><input type="date" value={f.eDate} onChange={(e) => set({ eDate: e.target.value })} className={field} data-testid="schedule-end-date" /><input type="time" value={f.eTime} onChange={(e) => set({ eTime: e.target.value })} className={`${field} w-32`} data-testid="schedule-end-time" /></div></Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Repeat">
              <select value={f.repeat} onChange={(e) => set({ repeat: e.target.value })} className={field} data-testid="schedule-repeat">
                <option value="none">Does not repeat</option><option value="daily">Every day</option><option value="weekly">Every week</option><option value="monthly">Every month</option>
              </select>
            </Field>
            {f.repeat !== "none" && <Field label="Repeat until (optional)"><input type="date" value={f.until} min={f.sDate} onChange={(e) => set({ until: e.target.value })} className={field} data-testid="schedule-repeat-until" /></Field>}
          </div>
          <Field label="Warn visitors with a banner">
            <select value={f.lead} onChange={(e) => set({ lead: e.target.value })} className={field} data-testid="schedule-lead">
              <option value="default">Use default ({defLead})</option>
              {LEADS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <WorldTimes start={start} end={end} tz={f.tz} />
          <label className="flex items-start gap-3 text-sm" data-testid="schedule-run-jobs-label">
            <Checkbox checked={f.runJobs} onCheckedChange={(v) => set({ runJobs: !!v })} data-testid="schedule-run-jobs" className="mt-0.5" />
            <span>Run automatic maintenance when it starts <span className="text-[#57534E]">(backup, clean-up, optimisation, file and link checks, service health)</span></span>
          </label>
          <label className="flex items-start gap-3 text-sm" data-testid="schedule-notify-label">
            <Checkbox checked={f.notify} onCheckedChange={(v) => set({ notify: !!v })} disabled={!subscriberCount} data-testid="schedule-notify" className="mt-0.5" />
            <span>Email everyone on the mailing list about this now <span className="text-[#57534E]">({subscriberCount} {subscriberCount === 1 ? "person" : "people"})</span></span>
          </label>
          <div className="flex justify-end gap-2 border-t border-black/10 pt-5">
            <button type="button" onClick={() => onOpenChange(false)} className="border border-black/20 px-5 py-3 text-sm font-semibold" data-testid="schedule-cancel">Cancel</button>
            <button type="button" onClick={save} disabled={busy} className="flex items-center gap-2 bg-[#0A0A0A] px-6 py-3 text-sm font-bold text-white hover:bg-[#E62314] disabled:opacity-50" data-testid="schedule-save">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}{win ? "Save changes" : "Schedule"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
